import {useCallback, useEffect, useRef, useState} from 'react';
import usePayinSessionQuery from 'src/api/graphql/usePayinSessionQuery';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {isEmbedded, isMobile} from 'src/utils/utils';
import {notifyInContextComplete} from 'src/v2/util/inContextCompletion';
import {urlRedirect} from '@stripe-internal/safe-links';
import {getStore} from 'src/v2/state/store';
import {getLogger} from 'src/utils/logger';
import {REFUNDABLE_REASONS} from './relayConstants';

const POLL_INTERVAL_MS = 2000;

const logger = getLogger();

type UsePaymentPollingOptions = {
  autoStart?: boolean;
};

export const usePaymentPollingAnyCrypto = (
  options?: UsePaymentPollingOptions,
): {startPolling: () => void} => {
  const store = getStore();
  const {clientSecret, sessionId} = useSessionContext();
  const returnUrl = store.useSelector(
    (state) => state.paymentDetails.returnUrl,
  );
  const [isPolling, setIsPolling] = useState(options?.autoStart ?? false);
  const hasStartedRef = useRef(options?.autoStart ?? false);

  const startPolling = useCallback(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    setIsPolling(true);
  }, []);

  const payinSessionData = usePayinSessionQuery({
    variables: {
      input: {
        crypto_payin_session: sessionId,
        client_secret: clientSecret,
      },
    },
    skip: !isPolling || !sessionId || !clientSecret,
    pollInterval: POLL_INTERVAL_MS,
    component: 'usePaymentPollingAnyCrypto',
  });

  const autoRedirectDisabled = isMobile() || isEmbedded();

  useEffect(() => {
    const payinSession = payinSessionData?.data?.v1CryptoInternalPayinSession;

    if (isPolling && payinSession) {
      const sessionState = payinSession.state;
      const relayStatus = payinSession.max_relay_request_status ?? null;

      store.setValue((state) => {
        state.state = payinSession.state;
        state.transactionDetails.blockchainTxId =
          payinSession.transaction_details?.blockchain_tx_id ?? null;
        state.anyCryptoDeposit.maxRelayRequestStatus = relayStatus;
        state.anyCryptoDeposit.relayFailReason =
          payinSession.relay_fail_reason ?? null;
        state.anyCryptoDeposit.relayRefundFailReason =
          payinSession.relay_refund_fail_reason ?? null;
        state.anyCryptoDeposit.relayFailureDetails =
          payinSession.relay_failure_details ?? null;
      });

      if (sessionState === 'fulfillment_complete') {
        setIsPolling(false);
        const shouldRedirect =
          !notifyInContextComplete() && !autoRedirectDisabled;
        logger.log('crypto_payin_session.screen.rendered', {
          screen: 'SUCCESS',
          message: `redirect: ${shouldRedirect}`,
        });
        if (shouldRedirect) {
          urlRedirect(returnUrl);
        }
      } else if (sessionState === 'error') {
        const failReason = payinSession.relay_fail_reason ?? null;
        if (
          !REFUNDABLE_REASONS.has(failReason ?? '') ||
          relayStatus === 'refund'
        ) {
          setIsPolling(false);
        }
      } else if (sessionState === 'canceled') {
        setIsPolling(false);
        store.setValue((state) => {
          state.screen = 'CANCELED';
        });
      }
    }
  }, [isPolling, payinSessionData, autoRedirectDisabled, store, returnUrl]);

  return {startPolling};
};
